<script setup lang="ts">
import { computed, onMounted } from 'vue';
import { useCookies } from '@vueuse/integrations/useCookies';
import { useSessionStore } from '@Stores';
import { useAToast } from '@Atoms';
import { useFieldsLogin, useLoginRequests } from '../composables';

const cookies = useCookies(['locale']);
const { fieldsSchema, formError, setForm } = useFieldsLogin();
const { notify, TOAST_TYPES } = useAToast();
const { onLoginSubmit, loading } = useLoginRequests();

const { reset } = useSessionStore();

const apiAuthCookies = ['af_api_logged_out', 'af_api_expired_session'];
const allCookies = computed(() => new Set(Object.keys(cookies.getAll())));

onMounted(() => initView());

// HELPERS
function initView() {
  const userComingFromOldAppLogout = apiAuthCookies.some((cookie) => allCookies.value.has(cookie));

  if (userComingFromOldAppLogout) onFinishLogoutFromOldApp();

  reset();
  setForm();
}

// EVENTS
function onFinishLogoutFromOldApp() {
  const TOAST_LOGGED_OUT = {
    title: 'auth.logout.goodbye.title',
    message: 'auth.logout.goodbye.message',
    type: TOAST_TYPES.SUCCESS,
  };

  const TOAST_EXPIRED_SESSION = {
    title: 'auth.logout.invalidSession.title',
    message: 'auth.logout.invalidSession.message',
    type: TOAST_TYPES.INFO,
  };

  const TOAST = allCookies.value.has('af_api_logged_out')
    ? TOAST_LOGGED_OUT
    : TOAST_EXPIRED_SESSION;

  cookies.remove('af_api_logged_out', { path: '/', domain: '.animalsfirst.test' });
  cookies.remove('af_api_expired_session', { path: '/', domain: '.animalsfirst.test' });

  notify(TOAST);
}
</script>

<template>
  <section class="flex min-h-full flex-1 flex-col justify-center px-6 py-12 lg:px-8">
    <AHeadingSection />

    <ANotMember v-if="false" />
    <!-- NOTE : Hidden until we release Free Trial -->

    <div class="mt-5 sm:mx-auto sm:w-full sm:max-w-xs">
      <FormTemplate
        :formError
        :schema="fieldsSchema"
        @onFormSubmit="onLoginSubmit"
      >
        <div
          v-if="formError"
          class="mb-5 flex h-fit items-center gap-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 align-middle text-xs text-red-600"
          pt:text="text-xs"
          severity="error"
          :closable="false"
        >
          <i class="pi pi-times-circle"></i>

          <div
            v-html="$t(formError)"
            class="errorDiv font-medium"
          ></div>
        </div>

        <Button
          class="af-button af-button-success w-full"
          type="submit"
          :loading
          :label="$t('auth.login.action')"
        />
      </FormTemplate>

      <MOtherAppsLogin />

      <AAuthLink
        message="auth.login.forgotPassword"
        route="/forgot-password"
      />
    </div>
  </section>
</template>

<style scoped>
:deep(.errorDiv > a) {
  @apply text-sky-500 transition-colors hover:text-sky-600 active:text-sky-700;
}
</style>
